import './main.css';
import * as env from './env.json';
import Map from 'ol/Map';
import View from 'ol/View';
import {Tile as TileLayer} from 'ol/layer';
import {TileDebug, OSM} from 'ol/source';
import {GeoJSON} from 'ol/format';
import {createStringXY} from 'ol/coordinate';
import {fromLonLat} from 'ol/proj';
import {Icon, Fill, Stroke, Style, Text} from 'ol/style';
import {createXYZ} from 'ol/tilegrid';
import {Control, Attribution, MousePosition, defaults as defaultControls} from 'ol/control';
import TileImage from 'ol/source/TileImage';
import sync from 'ol-hashed';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import {circular} from 'ol/geom/Polygon';

// --- Config ---

function apiUrl(path) {
  const base = env.sydneytrains.basePath || '';
  return `${base}${path}`;
}

// --- Data source ---

const sydney = fromLonLat([151.2061999882396, -33.8732161200895]);

const sourceSydneyTrains = new VectorSource({
  format: new GeoJSON(),
});

function updateFeatures(data) {
  const features = sourceSydneyTrains.getFormat().readFeatures(data, {
    dataProjection: 'EPSG:4326',
    featureProjection: 'EPSG:3857',
  });
  sourceSydneyTrains.clear(true);
  sourceSydneyTrains.addFeatures(features);
  sourceSydneyTrains.changed();
}

// --- SSE/WS connections with reconnection ---

let activeConnection = null;

function closeActiveConnection() {
  if (activeConnection) {
    if (activeConnection instanceof EventSource) {
      activeConnection.close();
    } else if (activeConnection instanceof WebSocket) {
      activeConnection.close();
    }
    activeConnection = null;
    sourceSydneyTrains.clear();
  }
}

function connectSSE() {
  closeActiveConnection();
  const source = new EventSource(apiUrl('/sydneytrainsnearme.sse'));
  activeConnection = source;
  let retryDelay = 1000;

  source.onmessage = function (event) {
    retryDelay = 1000;
    updateFeatures(event.data);
  };

  source.onerror = function () {
    console.warn(`SSE error, reconnecting in ${retryDelay}ms`);
    source.close();
    sourceSydneyTrains.clear();
    if (activeConnection === source) {
      setTimeout(() => {
        retryDelay = Math.min(retryDelay * 2, 30000);
        connectSSE();
      }, retryDelay);
    }
  };
}

function connectWS() {
  closeActiveConnection();
  const wsProto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const ws = new WebSocket(`${wsProto}//${window.location.host}${apiUrl('/sydneytrainsnearme.ws')}`);
  activeConnection = ws;
  let retryDelay = 1000;

  ws.onopen = () => {
    retryDelay = 1000;
  };

  ws.onmessage = (event) => {
    updateFeatures(event.data);
  };

  ws.onclose = () => {
    console.warn(`WebSocket closed, reconnecting in ${retryDelay}ms`);
    sourceSydneyTrains.clear();
    if (activeConnection === ws) {
      setTimeout(() => {
        retryDelay = Math.min(retryDelay * 2, 30000);
        connectWS();
      }, retryDelay);
    }
  };

  ws.onerror = () => {
    ws.close();
  };
}

function connectMode(mode) {
  if (mode === 'ws') {
    connectWS();
  } else {
    connectSSE();
  }
}

// --- Styles (cached per label) ---

const cachedIcon = new Icon({
  src: './train.svg',
  scale: 0.1,
});

const styleCache = new Map();

function pointStyleFunction(feature) {
  const label = feature.get('label');
  const trainId = feature.get('id');
  const textValue = label || trainId || '';
  let style = styleCache.get(textValue);
  if (!style) {
    style = new Style({
      image: cachedIcon,
      text: new Text({
        textAlign: 'left',
        offsetX: 14,
        text: textValue,
        font: 'bold 16px Helvetica,sans-serif',
        fill: new Fill({color: '#0180FD'}),
        stroke: new Stroke({color: [0,0,0,1], width: 5}),
      }),
      zIndex: Infinity,
    });
    styleCache.set(textValue, style);
  }
  return style;
}

// --- Layers ---

const sydneyTrainsLayer = new VectorLayer({
  source: sourceSydneyTrains,
  style: pointStyleFunction,
  declutter: true,
  minZoom: 12,
});

const sourceLocation = new VectorSource();
const locationLayer = new VectorLayer({
  source: sourceLocation,
});

const debugLayer = new TileLayer({
  source: new TileDebug({
    projection: 'EPSG:3857',
    tileGrid: createXYZ({maxZoom: 21}),
  }),
  visible: false,
});

const basemapLayer = new TileLayer({
  source: new OSM(),
});

const layers = [basemapLayer, sydneyTrainsLayer, debugLayer, locationLayer];

// Optional nearmap satellite layer
if (env.nearmap && env.nearmap.tile) {
  const nearmapLayer = new TileLayer({
    source: new TileImage({
      url: `${env.nearmap.tile}/tiles/v3/Vert/{z}/{x}/{y}.img?apikey=${env.nearmap.apikey}&tertiary=satellite`,
    }),
    visible: false,
  });
  layers.splice(1, 0, nearmapLayer);
}

// --- Map setup ---

const view = new View({
  center: sydney,
  zoom: 14,
});

const attribution = new Attribution({
  collapsible: false,
});

const map = new Map({
  target: 'map',
  layers: layers,
  controls: defaultControls({attribution: false}).extend([attribution]),
  view: view,
});

function onClick(id, callback) {
  document.getElementById(id).addEventListener('click', callback);
}

onClick('fly-to-sydney', function () {
  view.setCenter(sydney);
});

// --- Control panel toggle ---

const modeLabels = {
  'sse': 'Server-Sent Events',
  'ws': 'WebSocket',
};

const ctrlPanel = document.getElementById('ctrl-panel');
const ctrlToggle = document.getElementById('ctrl-panel-toggle');

ctrlToggle.addEventListener('click', () => {
  ctrlPanel.classList.toggle('collapsed');
});

function updateToggleLabel() {
  const selected = document.querySelector('input[name="mode"]:checked').value;
  ctrlToggle.textContent = 'Mode: ' + (modeLabels[selected] || selected);
}

// --- Mode selector ---

let currentMode = 'sse';

// Check URL param for initial mode
const urlParams = new URLSearchParams(window.location.search);
const modeParam = urlParams.get('mode');
if (modeParam === 'ws') {
  currentMode = 'ws';
  const wsRadio = document.querySelector('input[name="mode"][value="ws"]');
  if (wsRadio) wsRadio.checked = true;
  updateToggleLabel();
}

document.querySelectorAll("input[name='mode']").forEach((input) => {
  input.addEventListener('change', () => {
    updateToggleLabel();
    ctrlPanel.classList.add('collapsed');
    currentMode = document.querySelector('input[name="mode"]:checked').value;
    connectMode(currentMode);
  });
});

document.getElementById("checkbox-debug").addEventListener('change', function () {
  debugLayer.setVisible(this.checked);
});

// --- Start data connection ---

connectMode(currentMode);

// --- Mouse position ---

const mousePositionControl = new MousePosition({
  coordinateFormat: createStringXY(4),
  projection: 'EPSG:4326',
  target: document.getElementById('mouse-position'),
});

map.addControl(mousePositionControl);

// --- Geolocation ---

navigator.geolocation.watchPosition(
  function (pos) {
    const coords = [pos.coords.longitude, pos.coords.latitude];
    const accuracy = circular(coords, pos.coords.accuracy);
    sourceLocation.clear(true);
    sourceLocation.addFeatures([
      new Feature(accuracy.transform('EPSG:4326', map.getView().getProjection())),
      new Feature(new Point(fromLonLat(coords))),
    ]);
  },
  function (error) {
    console.warn(`Geolocation error: ${error.message}`);
  },
  {enableHighAccuracy: true},
);

const locate = document.createElement('div');
locate.className = 'ol-control ol-unselectable locate';
locate.innerHTML = '<button title="Locate me">&#x25CE;</button>';
locate.addEventListener('click', function () {
  if (!sourceLocation.isEmpty()) {
    map.getView().fit(sourceLocation.getExtent(), {
      maxZoom: 15,
      duration: 500,
    });
  }
});
map.addControl(new Control({element: locate}));

// Group zoom + locate in a flex container below the ctrl-panel
setTimeout(() => {
  const mapEl = map.getTargetElement();
  const container = document.createElement('div');
  container.className = 'left-controls';
  const zoom = mapEl.querySelector('.ol-zoom');
  const loc = mapEl.querySelector('.locate');
  if (zoom) container.appendChild(zoom);
  if (loc) container.appendChild(loc);
  const viewport = mapEl.querySelector('.ol-overlaycontainer-stopevent');
  if (viewport) viewport.appendChild(container);
  else mapEl.appendChild(container);
}, 0);

sync(map);
